import { useEffect, useRef } from 'react';

import { RunningExperiments, useExperiments } from 'state/experiments';

const CONTROL_VARIANT = 'control';
const TREATMENT_VARIANT = 'treatment';

type SignupFlowRedesignExperimentGate = {
  isReady: boolean;
  isTreatment: boolean;
};

type ResolveSignupFlowRedesignExperimentGateArgs = {
  isExperimentLoading: boolean;
  variantValue?: string;
};

/**
 * Resolves signup branch readiness without logging exposure.
 * Unresolved assignments stay unready while experiments are loading so we don't
 * mount control and then swap the Formik tree to treatment mid-signup.
 */
export const resolveSignupFlowRedesignExperimentGate = ({
  isExperimentLoading,
  variantValue,
}: ResolveSignupFlowRedesignExperimentGateArgs): SignupFlowRedesignExperimentGate => {
  const hasResolvedVariant = variantValue === CONTROL_VARIANT || variantValue === TREATMENT_VARIANT;
  const isReady = hasResolvedVariant || !isExperimentLoading;

  return {
    isReady,
    isTreatment: isReady && variantValue === TREATMENT_VARIANT,
  };
};

export const useSignupFlowRedesignExperimentGateSnapshot = (): SignupFlowRedesignExperimentGate => {
  const { getVariantValue, isLoading } = useExperiments();
  const signupFlowRedesignVariant = getVariantValue(RunningExperiments.SignupFlowRedesign);

  return resolveSignupFlowRedesignExperimentGate({
    isExperimentLoading: isLoading,
    variantValue: signupFlowRedesignVariant?.value,
  });
};

/**
 * Locks the selected signup branch for the lifetime of this hook mount. Exposure is
 * logged separately when the auth drawer is actually viewed on screen=sign-up.
 */
export const useSignupFlowRedesignExperimentGate = (): SignupFlowRedesignExperimentGate => {
  const lockedGateRef = useRef<SignupFlowRedesignExperimentGate | undefined>(undefined);
  const gate = useSignupFlowRedesignExperimentGateSnapshot();

  if (lockedGateRef.current) {
    return lockedGateRef.current;
  }

  if (gate.isReady) {
    lockedGateRef.current = gate;
  }

  return gate;
};

export const useEnableSignupFlowRedesignExperiment = () => {
  return useSignupFlowRedesignExperimentGate().isTreatment;
};

export const useSignupFlowRedesignExperiment = useEnableSignupFlowRedesignExperiment;

/**
 * Logs exposure whenever the auth drawer is on screen=sign-up. Safe to call across
 * repeated open/close cycles: ExperimentsProvider's logExposure already dedupes per
 * flag+variant, so this hook doesn't need its own dedupe.
 */
export const useExposeSignupFlowRedesignExperiment = (shouldExpose: boolean) => {
  const { logExposure } = useExperiments();

  useEffect(() => {
    if (!shouldExpose) {
      return;
    }

    logExposure(RunningExperiments.SignupFlowRedesign);
  }, [shouldExpose, logExposure]);
};
